import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { ShieldCheck, ArrowsClockwise, Lock, CheckCircle, X } from "@phosphor-icons/react";

const OUTCOME_STYLES = {
  success: {
    label: "Success",
    cls: "bg-[#1c2942] text-[#9bd17a] border-[#3a5a3a]",
    icon: CheckCircle,
  },
  fail: {
    label: "Failed",
    cls: "bg-[#1c2942] text-[#dda632] border-[#3a4f6e]",
    icon: X,
  },
  locked: {
    label: "Locked out",
    cls: "bg-[#1c2942] text-[#af3fa6] border-[#5a3a55]",
    icon: Lock,
  },
};

function timeAgo(iso) {
  if (!iso) return "—";
  const now = Date.now();
  const t = new Date(iso).getTime();
  const s = Math.max(1, Math.round((now - t) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-ZA", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function AdminAuditPage() {
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState(""); // "" | "success" | "fail" | "locked"
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const [s, r] = await Promise.all([
        api.get("/admin/audit/login/summary"),
        api.get("/admin/audit/login", {
          params: filter ? { outcome: filter, limit: 200 } : { limit: 200 },
        }),
      ]);
      setSummary(s.data);
      setRows(r.data.rows);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="px-10 py-12 text-[#f5ecd9]" data-testid="admin-audit-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="eyebrow mb-3">Security</div>
          <h1 className="font-display text-5xl tracking-tighter">Login activity</h1>
          <p className="mt-3 text-[#a8b2cc] max-w-xl">
            Every admin login attempt — success, failure, or lockout — captured with IP and
            timestamp. Use this to spot anomalous traffic before it escalates.
          </p>
        </div>
        <button
          onClick={load}
          className="btn-outline disabled:opacity-50"
          disabled={loading}
          data-testid="audit-refresh"
        >
          <ArrowsClockwise size={14} weight="bold" /> Refresh
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10" data-testid="audit-summary">
          <SummaryCard
            label="Successful logins"
            value={summary.totals.success}
            sub={summary.last_success ? `Last: ${timeAgo(summary.last_success.at)}` : "Never"}
            tone="success"
          />
          <SummaryCard
            label="Failed attempts"
            value={summary.totals.fail}
            sub={summary.last_fail ? `Last: ${timeAgo(summary.last_fail.at)}` : "None on record"}
            tone="fail"
          />
          <SummaryCard
            label="Lockouts triggered"
            value={summary.totals.locked}
            sub="Auto-resets every 15 min"
            tone="locked"
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-5">
        {[
          { key: "", label: "All" },
          { key: "success", label: "Success" },
          { key: "fail", label: "Failed" },
          { key: "locked", label: "Locked" },
        ].map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.16em] font-nav font-semibold rounded-full border transition-colors ${
              filter === f.key
                ? "bg-[#f5ecd9] text-[#070b14] border-[#f5ecd9]"
                : "border-[#2c3854] text-[#a8b2cc] hover:border-[#f5ecd9] hover:text-[#f5ecd9]"
            }`}
            data-testid={`audit-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {err && (
        <div className="rounded-2xl border border-[#af3fa6] text-[#af3fa6] p-3 mb-6 text-sm">
          {err}
        </div>
      )}

      <div className="rounded-[28px] border border-[#2c3854] overflow-hidden bg-[#141e32]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[#a8b2cc] border-b border-[#2c3854]">
              <th className="py-4 px-5 font-nav uppercase text-[0.7rem] tracking-[0.18em]">When</th>
              <th className="py-4 px-5 font-nav uppercase text-[0.7rem] tracking-[0.18em]">Email</th>
              <th className="py-4 px-5 font-nav uppercase text-[0.7rem] tracking-[0.18em]">Outcome</th>
              <th className="py-4 px-5 font-nav uppercase text-[0.7rem] tracking-[0.18em]">IP</th>
              <th className="py-4 px-5 font-nav uppercase text-[0.7rem] tracking-[0.18em]">User-Agent</th>
            </tr>
          </thead>
          <tbody data-testid="audit-table-body">
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="py-10 text-center text-[#a8b2cc]">
                  No entries found.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const cfg = OUTCOME_STYLES[r.outcome] || OUTCOME_STYLES.fail;
              const Icon = cfg.icon;
              return (
                <tr key={r.id} className="border-b border-[#2c3854]/60 last:border-0 hover:bg-[#0e1626]" data-testid={`audit-row-${r.id}`}>
                  <td className="py-3 px-5 align-top whitespace-nowrap">
                    <div className="text-[#f5ecd9]">{timeAgo(r.at)}</div>
                    <div className="text-[#5a6685] text-[0.7rem] mt-1">{fmt(r.at)}</div>
                  </td>
                  <td className="py-3 px-5 align-top">
                    <div className="text-[#f5ecd9]">{r.email}</div>
                    {r.detail && <div className="text-[#5a6685] text-[0.7rem] mt-1">{r.detail}</div>}
                  </td>
                  <td className="py-3 px-5 align-top">
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[0.7rem] uppercase tracking-[0.14em] font-semibold border ${cfg.cls}`}>
                      <Icon size={12} weight="bold" /> {cfg.label}
                    </span>
                  </td>
                  <td className="py-3 px-5 align-top text-[#a8b2cc] font-mono-accent text-[0.78rem]">{r.ip}</td>
                  <td className="py-3 px-5 align-top text-[#5a6685] text-[0.7rem] max-w-xs truncate" title={r.user_agent}>
                    {r.user_agent}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, sub, tone }) {
  const accent = tone === "success" ? "#9bd17a" : tone === "locked" ? "#af3fa6" : "#dda632";
  return (
    <div className="soft-card p-7" data-testid={`audit-summary-${tone}`}>
      <div className="eyebrow mb-3" style={{ color: accent }}>
        {label}
      </div>
      <div className="font-display text-5xl text-[#f5ecd9]">{value}</div>
      <div className="mt-3 text-[#a8b2cc] text-sm">{sub}</div>
    </div>
  );
}
